import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IPayment } from "../reducers.interfaces";

/**
 * Load available payment methods.
 *
 * Fetches the payment methods enabled for the current checkout session.
 * Available methods may vary based on basket contents, user location, and
 * site configuration. Also clears any existing payment errors.
 *
 * @returns {Promise<IPayment["method_type"][]>} Promise resolving to array of payment method types
 *
 * @example
 * // Load payment methods when user reaches payment step
 * dispatch(loadPaymentMethods());
 *
 * @example
 * // Reload payment methods after basket changes
 * const methods = await dispatch(loadPaymentMethods()).unwrap();
 * console.log("Available methods:", methods);
 *
 * @sideEffects
 * - Updates `data.payment_methods` with enabled methods via extraReducers
 * - Clears `data.payment_error` via extraReducers
 * - Logs to console in debug mode
 */
export const loadPaymentMethods = createAsyncThunk(
    "checkout/loadPaymentMethods",
    async (): Promise<IPayment["method_type"][]> => {
        console.debug("Loading available payment methods...");
        const methods = await checkoutAPI.listEnabledPaymentMethods();
        console.debug("Loaded available payment methods.");
        return methods as IPayment["method_type"][];
    },
);
